import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { Mail, MapPin, Phone, Clock } from "lucide-react";

const Footer = () => {
  const { t, lang } = useI18n();
  const base = `/${lang}`;
  const company = t("footer.sections.company");
  const legalCol = t("footer.sections.legal");
  const contact = t("footer.sections.contact");
  const legal = t("footer.legal");

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#070C18] border-t border-slate-800 text-slate-300"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link to={base} data-testid="footer-logo" className="flex items-center gap-3 mb-4">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-emerald-600 text-white font-bold font-mono text-sm">
                IA
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-slate-100 font-semibold text-[15px]">
                  {t("common.brand")}
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 font-mono">
                  {t("common.brandTagline")}
                </span>
              </span>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-4">
              {company.title}
            </h4>
            <ul className="flex flex-col gap-2 text-sm">
              <li><a href={`${base}#about`} data-testid="footer-link-about" className="hover:text-white">{company.links.about}</a></li>
              <li><a href={`${base}#how`} data-testid="footer-link-how" className="hover:text-white">{company.links.how}</a></li>
              <li><a href={`${base}#benefits`} data-testid="footer-link-benefits" className="hover:text-white">{company.links.benefits}</a></li>
              <li><a href={`${base}#faq`} data-testid="footer-link-faq" className="hover:text-white">{company.links.faq}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-4">
              {legalCol.title}
            </h4>
            <ul className="flex flex-col gap-2 text-sm">
              <li><Link to={`${base}/privacy`} data-testid="footer-privacy-link" className="hover:text-white">{legalCol.links.privacy}</Link></li>
              <li><Link to={`${base}/cookies`} data-testid="footer-cookies-link" className="hover:text-white">{legalCol.links.cookies}</Link></li>
              <li><Link to={`${base}/terms`} data-testid="footer-terms-link" className="hover:text-white">{legalCol.links.terms}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-4">
              {contact.title}
            </h4>
            <ul className="flex flex-col gap-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 text-slate-500 shrink-0" />
                <span className="text-slate-400">{contact.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-slate-500 shrink-0" />
                <a href={`mailto:${contact.email}`} data-testid="footer-email" className="hover:text-white">
                  {contact.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-slate-500 shrink-0" />
                <a href={`tel:${contact.phone.replace(/\s+/g, "")}`} data-testid="footer-phone" className="hover:text-white">
                  {contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={16} className="mt-0.5 text-slate-500 shrink-0" />
                <span className="text-slate-400">{contact.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-slate-500 leading-relaxed">
            <div className="space-y-1">
              <p className="text-slate-300 font-semibold">{legal.companyName}</p>
              <p>{legal.incorporated}</p>
              <p className="font-mono">{legal.ruc}</p>
              <p>{legal.registration}</p>
            </div>
            <div className="space-y-1">
              <p>{legal.supervision}</p>
              <p>{legal.license}</p>
              <p>{legal.jurisdiction}</p>
            </div>
          </div>
          <p className="mt-6 text-xs text-slate-500 leading-relaxed max-w-4xl">
            {legal.disclaimer}
          </p>
          <p className="mt-4 text-xs text-slate-500" data-testid="footer-copyright">
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
